/* global URLSearchParams */
import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Alert, FormGroup, FormFeedback, Input, Button, Label } from 'reactstrap'
import { Form, Field } from 'react-final-form'
import { FORM_ERROR } from 'final-form'
import { EMAIL } from 'suredone-common'
import { Loading } from '../../components'
import { FaSignInAlt as SignIn } from 'react-icons/fa'
import { AuthPage } from './AuthPage'
import { LoginAlerts } from './LoginAlerts'

import './Login.css'
import { useStores } from '../../stores/RootStore'
import { useQualifications, useQueryParams } from '../../hooks'

const debug = require('debug')('sd:Login')

const Login = () => {
  const location = useLocation().search
  const { stores } = useStores()
  // Store data in localstorage in case of comming from extarnal channel
  const { storedValue } = useQualifications()
  const { email, source } = storedValue
  const queryParams = useQueryParams()
  debug({ queryParams })
  const { auth } = stores
  const { emailForLogin } = auth || {}
  const params = new URLSearchParams(location)
  const confirmSuccess = !!queryParams['confirm-success']
  const useLegacyLogin = false // (window.serverConfig || {}).production === true

  const [tempEmail, setTempEmail] = useState('')

  const handleClickConfirmUser = () => {
    const currentHistory = window?.analyticsEventHistory
    if (window.analytics) {
      const currentEvent = 'Login: confirm user'
      const newHistory = [currentEvent, ...currentHistory].slice(0, 10)
      window.analyticsEventHistory = newHistory
      const fullFilled = {
        action: 'User clicks confirm user',
        description: 'Take user to the confirmation code page',
        pathHistory: window?.analyticsPathHistory,
        priorPath: window?.analyticsPathHistory?.[0],
        eventHistory: newHistory,
        priorEvent: newHistory?.[1]
      }
      window.analytics.track(currentEvent, fullFilled)
    }
  }

  const handleClickForgotPassword = (e) => {
    const currentHistory = window?.analyticsEventHistory
    if (window.analytics) {
      if (params.get('confirm-success')) {
        const currentEvent = 'Registration: Forgot password'
        const newHistory = [currentEvent, ...currentHistory].slice(0, 10)
        window.analyticsEventHistory = newHistory
        const fullFilled = {
          action: 'User clicks forgot password',
          description: 'User logs in during registration',
          pathHistory: window?.analyticsPathHistory,
          priorPath: window?.analyticsPathHistory?.[0],
          eventHistory: newHistory,
          priorEvent: newHistory?.[1]
        }
        window.analytics.track(currentEvent, fullFilled)
      } else {
        const currentEvent = 'Login: Forgot password'
        const newHistory = [currentEvent, ...currentHistory].slice(0, 10)
        window.analyticsEventHistory = newHistory
        const fullFilled = {
          action: 'User clicks forgot password',
          description: 'User logs in',
          pathHistory: window?.analyticsPathHistory,
          priorPath: window?.analyticsPathHistory?.[0],
          eventHistory: newHistory,
          priorEvent: newHistory?.[1]
        }
        window.analytics.track(currentEvent, fullFilled)
      }
    }
  }

  return (
    <>
      <LoginAlerts />
      <AuthPage
        headerSubtitle={
          <p className='d-flex flex-wrap w-100 justify-content-center align-items-baseline'>
            <span className='mr-2 w-100'>Don't have an account?</span>
            {useLegacyLogin
              ? (
                <Button color='link' className='px-1 py-0' href='https://www.suredone.com/contact-demo/' tag='a'>
                  Create One
                </Button>)
              : (
                <Button color='link' className='px-1 py-0' to={source ? '/register?skipdemo=true' : '/register'} tag={Link}>
                  Create One
                </Button>)}
          </p>
        }
        form={
          <Form
            initialValues={{
              email: (emailForLogin || email || ''),
              password: ''
            }}
            onSubmit={async values => {
              const { email, password } = values
              const form = document.getElementById('login-form')
              try {
                await auth.logIn(email, password)
              } catch (err) {
                debug({ err })
                return { [FORM_ERROR]: err.message }
              }
              if (confirmSuccess && window.analytics) {
                const currentEvent = 'Registration: login'
                const currentHistory = window?.analyticsEventHistory
                const newHistory = [currentEvent, ...currentHistory].slice(0, 10)
                window.analyticsEventHistory = newHistory
                const fullFilled = {
                  action: 'User clicks log in',
                  description: 'User logs in during registration',
                  pathHistory: window?.analyticsPathHistory,
                  priorPath: window?.analyticsPathHistory?.[0],
                  eventHistory: newHistory,
                  priorEvent: newHistory?.[1]
                }
                window.analytics.trackForm(form, currentEvent, fullFilled)
              }
            }}
            render={state => {
              // debug('FinalForm.render(state)', state)
              const { handleSubmit, submitError, hasValidationErrors, submitting, pristine } = state
              return (
                <form className='auth-form' id='login-form' onSubmit={handleSubmit}>
                  {params.get('confirm-success')
                    ? <Alert color='success'> You're all confirmed! Please log in. </Alert>
                    : null}
                  {params.get('password-reset')
                    ? <Alert color='success'> Success! Please log in using your new password. </Alert>
                    : null}

                  <FormGroup>
                    <div className='form-label-group'>
                      <Field
                        name='email'
                        validate={email => {
                          if (!email) return 'Email/username is required'
                          if (!EMAIL.test(email)) {
                            debug('Invalid Email')
                            // return 'Invalid Email'
                          }
                        }}
                      >
                        {({ input, meta }) => (
                          <>
                            <Input
                              {...input}
                              autoFocus
                              type='text'
                              id='email'
                              placeholder='Email / Username'
                              invalid={meta.error && meta.touched && !meta.pristine}
                              onChange={(e) => {
                                input.onChange((e.target.value).toLowerCase().trim())
                                setTempEmail(e.target.value)
                              }}
                            />
                            <Label for='email'>Email / Username</Label>
                            {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                          </>
                        )}
                      </Field>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <div className='form-label-group'>
                      <Field
                        name='password'
                        validate={password => {
                          if (!password) return 'Password is required'
                        }}
                      >
                        {({ input, meta }) => (
                          <>
                            <Input
                              {...input}
                              type='password'
                              id='password'
                              placeholder='Password'
                              invalid={meta.error && meta.touched && !meta.pristine}
                            />
                            <Label for='password'>Password</Label>
                            {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                          </>
                        )}
                      </Field>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    {submitError
                      ? <Alert color='danger'> {submitError} </Alert>
                      : null}
                    {submitting ? <Loading /> : null}
                    <Button type='submit' color='primary' disabled={submitting || pristine || hasValidationErrors} block>
                      <SignIn /> Log in
                    </Button>
                  </FormGroup>
                  <div className='text-center'>
                    <Button tag={Link} color='link' to={tempEmail !== '' ? `/reset-password?email=${encodeURIComponent(tempEmail)}` : '/reset-password'} onClick={() => handleClickForgotPassword()}>
                      Forgot Password?
                    </Button>
                    <span className='mx-2 d-none d-sm-inline-block'>·</span>
                    <Button tag={Link} color='link' to='/confirm' onClick={() => handleClickConfirmUser()}>
                      Confirm your user
                    </Button>
                  </div>
                  <div className='text-center'>
                    <Button id='attend-demonstration' type='button' color='link'>
                      Attend SureDone Demonstration
                    </Button>
                  </div>
                </form>
              )
            }}
          />
        }
      />
    </>
  )
}

export default Login

debug('loaded')
